import { term } from '../terms';
// Alpine behaviour for the component library (resources/views/components/ui).
// Registered in bootstrap.js; inert unless an x-ui component is on the page.

/** The admin library page: palette switcher and the section highlighted in the side nav. */
export function uiLibrary(modes = {}) {
    return {
        palette: document.documentElement.dataset.palette,
        section: null,
        init() {
            const io = new IntersectionObserver(
                (entries) => entries.forEach((e) => { if (e.isIntersecting) this.section = e.target.id; }),
                { rootMargin: '-30% 0px -60% 0px' },
            );
            this.$root.querySelectorAll('section.ui-sec').forEach((s) => io.observe(s));
        },
        pick(slug) {
            this.palette = slug;
            const root = document.documentElement;
            root.dataset.palette = slug;
            root.dataset.mode = modes[slug] || 'light';
            try { localStorage.setItem('tk-ui-palette', slug); } catch (e) {}
            requestAnimationFrame(() => {
                window.dispatchEvent(new CustomEvent('ui-relayout'));
                window.dispatchEvent(new CustomEvent('ui-palette', { detail: slug }));
            });
        },
    };
}

/** Segmented control: a thumb slides under the pressed option. `value` is x-modelable. */
export function uiSegmented(initial = null) {
    let placedAt = null; // not reactive: writing Alpine state from place() would re-run its effects
    return {
        value: initial,
        init() {
            this.$nextTick(() => this.place());
            this.$watch('value', () => this.place());
            window.addEventListener('resize', () => this.place());
            window.addEventListener('ui-relayout', () => this.place());
            document.fonts?.ready.then(() => this.place());
            // The thumb's size lives in an inline style the server never renders, so a Livewire morph of the
            // control (any re-render of its component, e.g. wire:init) strips it and leaves a 0-width thumb.
            // Put it back when that happens (only then: reacting to every style write would let two instances,
            // e.g. after a morph re-inits x-data, fight over the thumb forever), and when the control first gets
            // a size (hidden at init).
            const th = this.$refs.thumb;
            if (th) new MutationObserver(() => { if (!th.style.width) this.place(); }).observe(th, { attributes: true, attributeFilter: ['style'] });
            if (window.ResizeObserver) new ResizeObserver(() => this.place()).observe(this.$root);
        },
        place() {
            const on = this.$root.querySelector(`button[data-value="${CSS.escape(String(this.value))}"]`);
            const th = this.$refs.thumb;
            if (!on || !th || !on.offsetWidth) return;
            const width = `${on.offsetWidth}px`;
            const translate = `${on.offsetLeft}px 0`;
            // Unchanged: skip the write, which would otherwise wake the observer again.
            if (th.style.width === width && th.style.translate && placedAt === translate) return;
            placedAt = translate;
            // First placement (or after a morph wiped it): jump into place instead of growing from the left edge.
            const fresh = !th.style.width;
            if (fresh) th.style.transition = 'none';
            th.style.width = width;
            th.style.translate = translate;
            if (fresh) { th.offsetWidth; th.style.removeProperty('transition'); }
        },
    };
}

/** Tag input: Enter adds, Backspace once arms the last chip, twice removes it. `tags` is x-modelable. */
export function uiTagInput(initial = []) {
    return {
        tags: [...initial],
        query: '',
        armed: false,
        add() {
            const t = this.query.trim();
            if (t && !this.tags.includes(t)) this.tags.push(t);
            this.query = '';
            this.armed = false;
        },
        remove(i) { this.tags.splice(i, 1); this.armed = false; },
        key(e) {
            if (e.key === 'Enter') { e.preventDefault(); this.add(); return; }
            if (e.key === 'Backspace' && this.query === '' && this.tags.length) {
                if (this.armed) this.remove(this.tags.length - 1); else this.armed = true;
                return;
            }
            this.armed = false;
        },
    };
}

/** Copy field, optionally with a hidden secret that Reveal shows. */
export function uiCopy(text, masked = null) {
    return {
        done: false,
        shown: masked === null,
        get display() { return this.shown ? text : masked; },
        async copy() {
            this.done = true;
            try { await navigator.clipboard.writeText(text); } catch (e) {}
            setTimeout(() => (this.done = false), 1400);
        },
    };
}

/** One player for the whole app: covers and play buttons call $store.uiPlayer.play({...}). */
// The audio element lives outside the store: Alpine would wrap it in a proxy, and media methods
// called through a proxy throw.
let playerAudio = null;

export const uiPlayerStore = {
    open: false,
    expanded: false,
    paused: true,
    loading: false,
    error: null,
    time: 0,
    duration: 0,
    queue: [],
    index: -1,
    // A page that can hold the player in its own bar (the sound page on phones) sets this to its sound's key: the
    // island then gets a button that puts the player back there (window event ui-player-dock), or closes another sound.
    dockKey: null,
    get current() { return this.queue[this.index] ?? null; },
    get title() { return this.current?.title ?? ''; },
    get subtitle() { return this.current?.subtitle ?? ''; },
    get cover() { return this.current?.cover ?? null; },
    get progress() { return this.duration ? Math.min(1, this.time / this.duration) : 0; },
    clock(t) { if (!Number.isFinite(t)) return '0:00'; const s = Math.max(0, Math.floor(t)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; },
    el() {
        if (playerAudio) return playerAudio;
        const a = new Audio();
        a.preload = 'auto';
        a.addEventListener('timeupdate', () => { this.time = a.currentTime; });
        a.addEventListener('durationchange', () => { this.duration = a.duration || 0; });
        a.addEventListener('playing', () => { this.loading = false; this.paused = false; this.error = null; });
        a.addEventListener('pause', () => { this.paused = true; });
        a.addEventListener('waiting', () => { this.loading = true; });
        a.addEventListener('ended', () => { this.index < this.queue.length - 1 ? this.next() : (this.paused = true); });
        a.addEventListener('error', () => { this.loading = false; this.paused = true; this.error = 'This preview is no longer available.'; });
        playerAudio = a;
        return a;
    },
    key(t) { return t?.src ?? t?.title; },
    /** Same key as the button that started it (its audio URL, or its title when it has none). */
    isPlaying(key) { return this.open && !this.paused && this.key(this.current) === key; },
    isCurrent(key) { return this.open && this.key(this.current) === key; },
    /** Play a track ({ title, subtitle, cover, src }); the same one again pauses or resumes. */
    play(track) {
        if (this.current && this.key(this.current) === this.key(track)) { this.toggle(); return; }
        let i = this.queue.findIndex((t) => this.key(t) === this.key(track));
        if (i < 0) { this.queue.push(track); i = this.queue.length - 1; }
        this.start(i);
    },
    /** Replace the queue (e.g. a table's rows) and start at `start`. */
    playQueue(tracks, start = 0) { this.queue = [...tracks]; this.start(start); },
    start(i) {
        this.index = i;
        this.open = true;
        this.time = 0;
        this.duration = 0;
        this.error = null;
        const t = this.current;
        const a = this.el();
        if (!t?.src) { a.pause(); this.paused = true; this.error = `No preview for this ${term('sound')}.`; return; }
        this.loading = true;
        a.src = t.src;
        a.play().catch(() => { this.loading = false; this.paused = true; });
    },
    toggle() {
        const a = this.el();
        if (!this.current?.src) return;
        if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
    },
    seek(fraction) { const a = this.el(); if (this.duration) a.currentTime = Math.max(0, Math.min(1, fraction)) * this.duration; },
    skip(seconds) { const a = this.el(); if (this.duration) a.currentTime = Math.max(0, Math.min(this.duration, a.currentTime + seconds)); },
    next() { if (this.index < this.queue.length - 1) this.start(this.index + 1); },
    prev() { if (this.time > 3) { this.seek(0); return; } if (this.index > 0) this.start(this.index - 1); },
    close() { playerAudio?.pause(); this.open = false; this.expanded = false; },
};

/** Covers from the TikTok CDN expire: swap a broken one inside a ui- component for a quiet tile. */
const FALLBACK_COVER = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="48" height="48" fill="rgba(128,128,128,.16)"/><path d="M21 31.5V17l10-2v13.5" fill="none" stroke="rgba(128,128,128,.55)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="18.5" cy="31.5" r="2.5" fill="none" stroke="rgba(128,128,128,.55)" stroke-width="2"/><circle cx="28.5" cy="28.5" r="2.5" fill="none" stroke="rgba(128,128,128,.55)" stroke-width="2"/></svg>');
document.addEventListener('error', (e) => {
    const img = e.target;
    if (img?.tagName !== 'IMG' || img.src === FALLBACK_COVER || !img.closest('[class^="ui-"], [class*=" ui-"]')) return;
    img.src = FALLBACK_COVER;
}, true);
